import { addDays, addHours, parseISO, startOfHour } from 'date-fns'

interface Span {
  datetimeStart: string
  datetimeEnd: string
}

export interface TimelineAxis {
  start: number
  end: number
  /** Whole hours to label, as timestamps. */
  ticks: number[]
}

const HOUR = 3_600_000
const TICK_STEPS = [1, 2, 3, 4, 6]
const MAXIMUM_TICKS = 7

const ceilToHour = (moment: number) => {
  const floor = startOfHour(moment).getTime()
  return floor === moment ? floor : addHours(floor, 1).getTime()
}

/**
 * The part of the day the spans cover, widened to whole hours, so a day
 * driven 06–14 is not squeezed into a third of the strip.
 */
export const timelineAxis = (spans: Span[], date: string): TimelineAxis => {
  const dayStart = parseISO(date).getTime()
  const dayEnd = addDays(parseISO(date), 1).getTime()

  let start = dayStart
  let end = dayEnd
  if (spans.length > 0) {
    const first = Math.min(
      ...spans.map(s => new Date(s.datetimeStart).getTime())
    )
    const last = Math.max(...spans.map(s => new Date(s.datetimeEnd).getTime()))
    start = Math.max(dayStart, startOfHour(first).getTime())
    end = Math.min(dayEnd, Math.max(ceilToHour(last), start + HOUR))
  }

  const hours = Math.round((end - start) / HOUR)
  const step =
    TICK_STEPS.find(candidate => hours / candidate < MAXIMUM_TICKS) ?? 6
  const ticks: number[] = []
  for (let tick = start; tick <= end; tick = addHours(tick, step).getTime())
    ticks.push(tick)

  return { start, end, ticks }
}

/** The span under a moment, or the nearest one when the moment falls in a gap. */
export const spanAt = (spans: Span[], moment: number): number | null => {
  let nearest: number | null = null
  let nearestDistance = Infinity

  spans.forEach((span, index) => {
    const start = new Date(span.datetimeStart).getTime()
    const end = new Date(span.datetimeEnd).getTime()
    const distance =
      moment < start ? start - moment : moment > end ? moment - end : 0
    if (distance < nearestDistance) {
      nearest = index
      nearestDistance = distance
    }
  })

  return nearest
}

/** The next item of a tone after the current one, wrapping around the day. */
export const nextOfTone = (
  items: { tone?: string | null }[],
  tone: string,
  current: number | null
): number | null => {
  const start = current === null ? 0 : current + 1
  for (let offset = 0; offset < items.length; offset++) {
    const index = (start + offset) % items.length
    if (items[index]?.tone === tone) return index
  }
  return null
}
